import { useNavigate } from "react-router-dom";
import { FaFacebook, FaLinkedin, FaInstagramSquare, FaWhatsapp } from "react-icons/fa";
import { motion } from "framer-motion";

const socials = [
  {
    icon: FaFacebook,
    label: "Facebook",
    url: "https://www.facebook.com/people/ClaimPro-Assist/61559865797607/?mibextid=LQQJ4d",
    color: "#1877F2",
  },
  {
    icon: FaLinkedin,
    label: "LinkedIn",
    url: "https://www.linkedin.com/company/claim-pro-assist/",
    color: "#0A66C2",
  },
  {
    icon: FaInstagramSquare,
    label: "Instagram",
    url: "https://www.instagram.com/claimproassist?utm_source=ig_web_button_share_sheet&igsh=ODdmZWVhMTFiMw==",
    color: "#E1306C",
  },
  {
    icon: FaWhatsapp,
    label: "WhatsApp",
    url: "https://whatsapp.com/channel/0029Vaf9dkzEgGfM3tCiUJ3Z",
    color: "#25D366",
  },
];

const navLinks = [
  { label: "Home", scrollTo: "home" },
  { label: "About", scrollTo: "about" },
  { label: "Our Impact", scrollTo: "impact" },
  { label: "Services", scrollTo: "services" },
  { label: "News", scrollTo: "news" },
];

const serviceLinks = [
  "Crane & Hydra",
  "Recovery Van",
  "Legal & Advocate",
  "Mechanic On-Site",
  "Workshop",
  "Insurance Renewal",
  "PUC Renewal",
  "Challan Assistance",
];

const legalLinks = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Use", path: "/terms" },
  { label: "Refund Policy", path: "/refund-policy" },
];

export default function Footer({ onHomeClick, onAboutClick, onNewsClick }) {
  const navigate = useNavigate();

  const openLink = (url) => window.open(url, "_blank", "noopener,noreferrer");

  const handleNavClick = (scrollTo) => {
    if (scrollTo === "home" && onHomeClick) return onHomeClick();
    if (scrollTo === "about" && onAboutClick) return onAboutClick();
    if (scrollTo === "news" && onNewsClick) return onNewsClick();
    const el = document.getElementById(scrollTo);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      style={{
        background: "#020617",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        position: "relative",
        overflow: "hidden",
        fontFamily: "Satoshi, sans-serif",
      }}
    >
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: "-10%",
          left: "20%",
          width: 500,
          height: 300,
          borderRadius: "50%",
          background: "#21A6E9",
          opacity: 0.04,
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />

      {/* ── Top CTA strip ─────────────────────────────────────────── */}
      <div
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          padding: "40px 24px",
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 20,
          }}
        >
          <div>
            <p
              style={{
                color: "#fff",
                fontSize: "clamp(18px, 2.5vw, 26px)",
                fontWeight: 600,
                margin: "0 0 4px",
                lineHeight: 1.2,
              }}
            >
              Stuck on the road?
            </p>
            <p style={{ color: "#475569", fontSize: 13, margin: 0 }}>
              Our team is available 24 × 7 — call or request via the app.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {/* Phone */}
            <a
              href="tel:+917800784700"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#21A6E9",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                padding: "10px 20px",
                borderRadius: 10,
                textDecoration: "none",
                letterSpacing: "0.02em",
              }}
            >
              <i className="ti ti-phone" aria-hidden="true" style={{ fontSize: 16 }} />
              +91 7800 78 4700
            </a>

            {/* Contact page */}
            <button
              onClick={() => navigate("/ContactUs")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "transparent",
                color: "#94a3b8",
                fontSize: 13,
                fontWeight: 600,
                padding: "10px 20px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.1)",
                cursor: "pointer",
                letterSpacing: "0.02em",
              }}
            >
              Contact Us
              <i className="ti ti-arrow-right" aria-hidden="true" style={{ fontSize: 14 }} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Main footer grid ───────────────────────────────────────── */}
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "56px 24px 48px",
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1.5fr 1.2fr",
          gap: "40px 48px",
        }}
        className="cp-footer-grid"
      >
        {/* Col 1 — Brand */}
        <div>
          <img
            src="https://claimpro-dev.s3.ap-south-1.amazonaws.com/Assets/navbar/logo-navbar-compressed.png"
            alt="ClaimPro Assist"
            style={{ height: 48, width: "auto", objectFit: "contain", marginBottom: 16 }}
          />
          <p
            style={{
              color: "#475569",
              fontSize: 13,
              lineHeight: 1.75,
              margin: "0 0 24px",
              maxWidth: 280,
            }}
          >
            A tech-driven roadside assistance and vehicle recovery service provider — committed to supporting drivers and fleet owners across India during accidents, breakdowns and emergencies.
          </p>

          {/* Socials */}
          <div style={{ display: "flex", gap: 10 }}>
            {socials.map((s) => (
              <motion.button
                key={s.label}
                aria-label={s.label}
                whileHover={{ y: -3, scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openLink(s.url)}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#64748b",
                  fontSize: 16,
                  transition: "color 0.2s, border-color 0.2s, background 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = s.color;
                  e.currentTarget.style.borderColor = s.color + "55";
                  e.currentTarget.style.background = s.color + "12";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#64748b";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                  e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                }}
              >
                <s.icon />
              </motion.button>
            ))}
          </div>
        </div>

        {/* Col 2 — Quick links */}
        <div>
          <p
            style={{
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              margin: "0 0 20px",
            }}
          >
            Quick Links
          </p>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {navLinks.map((link) => (
              <li key={link.label}>
                <button
                  onClick={() => handleNavClick(link.scrollTo)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#475569",
                    fontSize: 13,
                    fontFamily: "Satoshi, sans-serif",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#21A6E9")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#475569")}
                >
                  <i className="ti ti-chevron-right" aria-hidden="true" style={{ fontSize: 12, opacity: 0.5 }} />
                  {link.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3 — Services */}
        <div>
          <p
            style={{
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              margin: "0 0 20px",
            }}
          >
            Services
          </p>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {serviceLinks.map((s) => (
              <li key={s}>
                <span
                  style={{
                    color: "#475569",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <i className="ti ti-chevron-right" aria-hidden="true" style={{ fontSize: 12, opacity: 0.5 }} />
                  {s}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 4 — Contact & Legal */}
        <div>
          <p
            style={{
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              margin: "0 0 20px",
            }}
          >
            Contact
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 32 }}>
            <a
              href="tel:+917800784700"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                color: "#475569",
                fontSize: 13,
                textDecoration: "none",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#21A6E9")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#475569")}
            >
              <i className="ti ti-phone" aria-hidden="true" style={{ fontSize: 15, marginTop: 1, flexShrink: 0, color: "#21A6E9" }} />
              +91 7800 78 4700
            </a>

            <a
              href="mailto:info@claimpro.in"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                color: "#475569",
                fontSize: 13,
                textDecoration: "none",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#21A6E9")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#475569")}
            >
              <i className="ti ti-mail" aria-hidden="true" style={{ fontSize: 15, marginTop: 1, flexShrink: 0, color: "#21A6E9" }} />
              info@claimpro.in
            </a>

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                color: "#475569",
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              <i className="ti ti-map-pin" aria-hidden="true" style={{ fontSize: 15, marginTop: 2, flexShrink: 0, color: "#21A6E9" }} />
              BCV ClaimPro Assist Pvt. Ltd.,<br />Gurugram, India
            </div>
          </div>

          {/* Legal links */}
          <p
            style={{
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              margin: "0 0 14px",
            }}
          >
            Legal
          </p>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {legalLinks.map((l) => (
              <li key={l.label}>
                <button
                  onClick={() => navigate(l.path)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#475569",
                    fontSize: 13,
                    fontFamily: "Satoshi, sans-serif",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#21A6E9")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#475569")}
                >
                  <i className="ti ti-chevron-right" aria-hidden="true" style={{ fontSize: 12, opacity: 0.5 }} />
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Bottom bar ─────────────────────────────────────────────── */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.06)",
          padding: "20px 24px",
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <p style={{ color: "#334155", fontSize: 12, margin: 0 }}>
            © {new Date().getFullYear()} BCV ClaimPro Assist Pvt. Ltd. All rights reserved.
          </p>
          <p style={{ color: "#1e293b", fontSize: 12, margin: 0 }}>
            Made with care for India's commercial fleet operators.
          </p>
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 1023px) {
          .cp-footer-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 599px) {
          .cp-footer-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </footer>
  );
}